/**
 * Strategy Factory Functions
 *
 * Enables runtime switching of path and routing strategies.
 * Used by EdgeRenderingService and Sidebar UI.
 */

import type { EdgePathType, EdgeRoutingType } from '../types';
import { BezierPathStrategy } from './path/bezier-path';
import { OrthogonalPathStrategy } from './path/orthogonal-path';
import { RoundedElbowPathStrategy } from './path/rounded-elbow-path';
import type { IEdgePathStrategy } from './path/types';
import { DirectRoutingStrategy } from './routing/direct-routing';
import type { IEdgeRoutingStrategy } from './routing/types';

/**
 * Creates a path strategy instance for the given type
 *
 * @param type - Path strategy type
 * @returns Path strategy instance
 * @throws Error for unimplemented strategies
 *
 * @example
 * ```typescript
 * const strategy = createPathStrategy('bezier');
 * const path = strategy.calculatePath(sourceBound, targetBound);
 * ```
 */
export function createPathStrategy(type: EdgePathType): IEdgePathStrategy {
  switch (type) {
    case 'bezier':
      return new BezierPathStrategy();

    case 'rounded-elbow':
      return new RoundedElbowPathStrategy();

    case 'orthogonal':
      return new OrthogonalPathStrategy();

    default:
      // Fallback to default
      console.warn(`Unknown path type "${type}", falling back to bezier`);
      return new BezierPathStrategy();
  }
}

/**
 * Creates a routing strategy instance for the given type
 *
 * @param type - Routing strategy type
 * @returns Routing strategy instance
 * @throws Error for unimplemented strategies
 *
 * @example
 * ```typescript
 * const strategy = createRoutingStrategy('direct');
 * const routedEdges = strategy.route(edges, elements);
 * ```
 */
export function createRoutingStrategy(
  type: EdgeRoutingType
): IEdgeRoutingStrategy {
  switch (type) {
    case 'direct':
      return new DirectRoutingStrategy();

    case 'avoid-overlap':
      // Future implementation
      throw new Error('CollisionAvoidanceStrategy not yet implemented');

    case 'astar':
      // Future implementation
      throw new Error('AStarRoutingStrategy not yet implemented');

    default:
      // Fallback to default
      console.warn(`Unknown routing type "${type}", falling back to direct`);
      return new DirectRoutingStrategy();
  }
}

/**
 * Checks if a path strategy type is implemented
 */
export function isPathStrategyAvailable(type: EdgePathType): boolean {
  return type === 'bezier' || type === 'rounded-elbow' || type === 'orthogonal';
}

/**
 * Checks if a routing strategy type is implemented
 */
export function isRoutingStrategyAvailable(type: EdgeRoutingType): boolean {
  return type === 'direct';
}

/**
 * Returns all available path strategy types
 */
export function getAvailablePathStrategies(): EdgePathType[] {
  return ['bezier', 'rounded-elbow', 'orthogonal'];
}

/**
 * Returns all available routing strategy types
 */
export function getAvailableRoutingStrategies(): EdgeRoutingType[] {
  return ['direct'];
  // Future: return ['direct', 'avoid-overlap', 'astar'];
}
